import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Search, Users, Star, Plus, ArrowRight, Trash2, Loader2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COUNTRY_FLAGS = { TG: "🇹🇬", GA: "🇬🇦", FR: "🇫🇷", RW: "🇷🇼" };

function detectCountry(phone) {
  if (!phone) return null;
  const p = phone.replace(/\s/g, "");
  if (p.startsWith("+228")) return "TG";
  if (p.startsWith("+241")) return "GA";
  if (p.startsWith("+33")) return "FR";
  if (p.startsWith("+250")) return "RW";
  return null;
}

export default function Contacts() {
  const { user } = useAuth();
  const [saved, setSaved] = useState([]);
  const [derived, setDerived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const loadAll = async () => {
    if (!user) { setLoading(false); return; }
    try {
      const [s, d] = await Promise.all([
        api.get("/me/contacts").catch(() => ({ data: [] })),
        api.get("/me/beneficiaries").catch(() => ({ data: [] })),
      ]);
      setSaved(s.data || []);
      setDerived(d.data || []);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleFavorite = async (contact) => {
    try {
      const { data } = await api.patch(`/me/contacts/${contact.id}`, { favorite: !contact.favorite });
      setSaved((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (contact) => {
    if (!window.confirm(`Supprimer « ${contact.name} » de vos contacts ?`)) return;
    try {
      await api.delete(`/me/contacts/${contact.id}`);
      setSaved((prev) => prev.filter((x) => x.id !== contact.id));
      toast.success("Contact supprimé");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const saveDerivedAsContact = async (b) => {
    try {
      const { data } = await api.post("/me/contacts", {
        name: b.beneficiary_name,
        phone: b.beneficiary_phone || null,
        email: b.beneficiary_email || null,
        account: b.beneficiary_account || null,
        reception_method: b.reception_method,
        corridor: b.corridor,
        favorite: false,
      });
      setSaved((prev) => [data, ...prev]);
      toast.success("Contact enregistré dans le carnet");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const onAdded = (contact) => {
    setSaved((prev) => [contact, ...prev]);
    setShowAdd(false);
  };

  const onUpdated = (contact) => {
    setSaved((prev) => prev.map((c) => (c.id === contact.id ? contact : c)));
    setEditTarget(null);
  };

  const q = query.trim().toLowerCase();
  const matches = (text) => !q || (text || "").toLowerCase().includes(q);
  const filteredSaved = saved
    .filter((c) => matches(c.name) || matches(c.phone) || matches(c.email))
    .sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
  const filteredDerived = derived
    .filter((b) => matches(b.beneficiary_name) || matches(b.beneficiary_phone) || matches(b.beneficiary_email))
    // Hide derived contacts that already exist in saved (same phone/email)
    .filter((b) => !saved.some((s) =>
      (s.phone && s.phone === b.beneficiary_phone) ||
      (s.email && s.email === b.beneficiary_email) ||
      (s.name && b.beneficiary_name && s.name.toLowerCase() === b.beneficiary_name.toLowerCase())
    ));

  return (
    <div className="min-h-screen sl-hero-bg pt-28 pb-24">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Users size={22} /></span>
            <div>
              <h1 className="font-heading text-3xl font-bold">Mes contacts</h1>
              <p className="text-gray-600 text-sm mt-1">Bénéficiaires enregistrés et favoris pour aller plus vite.</p>
            </div>
          </div>
          {user && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="sl-btn-primary text-sm"
              data-testid="contacts-add-btn"
            >
              <Plus size={14} /> Ajouter un contact
            </button>
          )}
        </div>

        <div className="sl-card mb-5 flex items-center gap-2">
          <Search size={16} className="text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un contact (nom ou numéro)…"
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid="contacts-search"
          />
        </div>

        {loading ? (
          <div className="sl-card text-center text-gray-500">Chargement…</div>
        ) : !user ? (
          <div className="sl-card text-center text-sm text-gray-600">
            Connectez-vous pour retrouver vos contacts sur tous vos appareils.
            <div className="mt-3"><Link to="/login" className="sl-btn-primary text-xs">Se connecter</Link></div>
          </div>
        ) : (
          <>
            {filteredSaved.length > 0 && (
              <>
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-gray-500 mb-3 mt-1">
                  Carnet enregistré
                </h2>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="contacts-saved">
                  {filteredSaved.map((c, i) => (
                    <SavedContactCard
                      key={c.id}
                      contact={c}
                      index={i}
                      onToggleFav={() => toggleFavorite(c)}
                      onDelete={() => remove(c)}
                      onEdit={() => setEditTarget(c)}
                    />
                  ))}
                </div>
              </>
            )}

            {filteredDerived.length > 0 && (
              <>
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-gray-500 mb-3 mt-8">
                  Récents (à enregistrer)
                </h2>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="contacts-derived">
                  {filteredDerived.map((b, i) => (
                    <DerivedContactCard
                      key={`${b.beneficiary_phone || b.beneficiary_email || i}`}
                      b={b}
                      index={i}
                      onSave={() => saveDerivedAsContact(b)}
                    />
                  ))}
                </div>
              </>
            )}

            {filteredSaved.length === 0 && filteredDerived.length === 0 && (
              <div className="sl-card text-center text-sm text-gray-500" data-testid="contacts-empty">
                Aucun contact pour le moment. Cliquez sur <span className="font-semibold">Ajouter un contact</span> ou
                effectuez un premier transfert.
              </div>
            )}
          </>
        )}

        <Link to="/envoyer" className="sl-btn-primary mt-8 inline-flex">
          <Plus size={14} /> Nouveau transfert
        </Link>
      </div>

      {showAdd && (
        <AddContactModal onClose={() => setShowAdd(false)} onAdded={onAdded} />
      )}
      {editTarget && (
        <EditContactModal contact={editTarget} onClose={() => setEditTarget(null)} onUpdated={onUpdated} />
      )}
    </div>
  );
}

/* ============ Saved contact card ============ */
function SavedContactCard({ contact, index, onToggleFav, onDelete, onEdit }) {
  const country = detectCountry(contact.phone);
  return (
    <div className="sl-card sl-card-hoverable flex items-start gap-3" data-testid={`contact-saved-${index}`}>
      <div className="w-10 h-10 rounded-full bg-[var(--sl-primary)]/10 text-[var(--sl-primary)] grid place-items-center font-bold flex-shrink-0">
        {(contact.name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm truncate">{contact.name || "Sans nom"}</span>
          {country && <span title={country}>{COUNTRY_FLAGS[country]}</span>}
        </div>
        <div className="text-xs text-gray-500 font-mono truncate">{contact.phone || contact.email || "—"}</div>
        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
          <Link
            to={`/envoyer?corridor=${contact.corridor || "tg_ga"}&contact_id=${contact.id}`}
            className="text-xs text-[var(--sl-primary)] font-medium hover:underline inline-flex items-center gap-1"
          >
            Envoyer <ArrowRight size={11} />
          </Link>
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-gray-500 hover:text-[var(--sl-primary)] inline-flex items-center gap-1"
            data-testid={`contact-edit-${index}`}
          >
            <Pencil size={11} /> Modifier
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-xs text-gray-400 hover:text-red-600 inline-flex items-center gap-1"
            data-testid={`contact-delete-${index}`}
          >
            <Trash2 size={11} /> Supprimer
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onToggleFav}
        className={`p-1.5 rounded-full transition ${contact.favorite ? "text-amber-500" : "text-gray-300 hover:text-amber-500"}`}
        aria-label="Favori"
        data-testid={`contact-fav-${index}`}
      >
        <Star size={16} fill={contact.favorite ? "currentColor" : "none"} />
      </button>
    </div>
  );
}

/* ============ Derived (auto-from-tx) contact card ============ */
function DerivedContactCard({ b, index, onSave }) {
  const country = detectCountry(b.beneficiary_phone);
  return (
    <div className="sl-card sl-card-hoverable flex items-start gap-3 opacity-95" data-testid={`contact-derived-${index}`}>
      <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-500 grid place-items-center font-bold flex-shrink-0">
        {(b.beneficiary_name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm truncate">{b.beneficiary_name || "Sans nom"}</span>
          {country && <span title={country}>{COUNTRY_FLAGS[country]}</span>}
        </div>
        <div className="text-xs text-gray-500 font-mono truncate">{b.beneficiary_phone || b.beneficiary_email || "—"}</div>
        <button
          type="button"
          onClick={onSave}
          className="mt-1 text-xs font-medium text-[var(--sl-primary)] hover:underline inline-flex items-center gap-1"
          data-testid={`contact-save-derived-${index}`}
        >
          <Plus size={11} /> Enregistrer dans le carnet
        </button>
      </div>
    </div>
  );
}

/* ============ Modal: add contact ============ */
function AddContactModal({ onClose, onAdded }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [favorite, setFavorite] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmedName = (name || "").trim();
    if (!trimmedName) { toast.error("Le nom est obligatoire"); return; }
    if (!phone && !email) { toast.error("Téléphone ou email requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/me/contacts", {
        name: trimmedName,
        phone: (phone || "").trim() || null,
        email: (email || "").trim() || null,
        favorite,
      });
      toast.success("Contact ajouté");
      onAdded(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="contacts-add-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Nouveau contact</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1"
            aria-label="Fermer"
            data-testid="contacts-add-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="sl-label">Nom complet</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="sl-input"
              placeholder="Ex. Jean Dupont"
              data-testid="contacts-add-name"
            />
          </div>
          <div>
            <label className="sl-label">Téléphone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="sl-input"
              placeholder="+228 90 00 00 00"
              data-testid="contacts-add-phone"
            />
          </div>
          <div>
            <label className="sl-label">Email (optionnel)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="sl-input"
              placeholder="jean@exemple.com"
              data-testid="contacts-add-email"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
            <input type="checkbox" checked={favorite} onChange={(e) => setFavorite(e.target.checked)} data-testid="contacts-add-favorite" />
            <Star size={14} className="text-amber-500" /> Ajouter aux favoris
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="sl-btn-outline text-sm"
            data-testid="contacts-add-cancel"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="sl-btn-primary text-sm disabled:opacity-60"
            data-testid="contacts-add-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============ Modal: edit contact ============ */
function EditContactModal({ contact, onClose, onUpdated }) {
  const [name, setName] = useState(contact.name || "");
  const [phone, setPhone] = useState(contact.phone || "");
  const [email, setEmail] = useState(contact.email || "");
  const [favorite, setFavorite] = useState(!!contact.favorite);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmedName = (name || "").trim();
    if (!trimmedName) { toast.error("Le nom est obligatoire"); return; }
    if (!phone && !email) { toast.error("Téléphone ou email requis"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/me/contacts/${contact.id}`, {
        name: trimmedName,
        phone: (phone || "").trim() || null,
        email: (email || "").trim() || null,
        favorite,
      });
      toast.success("Contact modifié");
      onUpdated(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="contacts-edit-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Modifier le contact</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Fermer" data-testid="contacts-edit-close">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="sl-label">Nom complet</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="sl-input" data-testid="contacts-edit-name" />
          </div>
          <div>
            <label className="sl-label">Téléphone</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="sl-input" data-testid="contacts-edit-phone" />
          </div>
          <div>
            <label className="sl-label">Email (optionnel)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="sl-input" data-testid="contacts-edit-email" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
            <input type="checkbox" checked={favorite} onChange={(e) => setFavorite(e.target.checked)} data-testid="contacts-edit-favorite" />
            <Star size={14} className="text-amber-500" /> Ajouter aux favoris
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="sl-btn-outline text-sm" data-testid="contacts-edit-cancel">Annuler</button>
          <button type="button" onClick={save} disabled={saving} className="sl-btn-primary text-sm disabled:opacity-60" data-testid="contacts-edit-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
            Enregistrer les modifications
          </button>
        </div>
      </div>
    </div>
  );
}
